import { Audit } from "models/generateAuditSchemas";
import { GaiaObject } from "models/generateGaiaObjSchemas";
import { CustomAttributes } from "models/generateCustomAttributesSchema";
import { ProgressUpdateAttachment } from "models/schemas";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { generateDynamicAttributesDataMap } from "utils/dynamicAttributesUtils";
import { getEntityNameFromEntityCompositeKey } from "features/ui-content/utils";
import { toDateString, toUnixEpochTime } from "utils/date-format";
import { getQueryErrorMessage } from "utils/error-message-utils";
import { ClosingRequirementResult } from "components/closing-requirements/definitions";
import {
  CloseDateAttribute,
  ProgressUpdateCategory,
  ProgressUpdateFormValues,
  ProgressUpdateSaveResult,
} from "./definitions";
import {
  UpdateAuditProgressRequest,
  UpdateGaiaObjectProgressRequest,
} from "./endpoints";

const CLOSE_DATE_ATTRIBUTES: CloseDateAttribute[] = ["closeDate", "closedDate"];

export const closeDateAttribute = (
  customAttributes?: CustomAttributes,
): CloseDateAttribute | null =>
  CLOSE_DATE_ATTRIBUTES.find((name) =>
    customAttributes?.some((attribute) => attribute.attributeName === name),
  ) ?? null;

export const initialFormValues = (
  category: ProgressUpdateCategory,
  record: Audit | GaiaObject | undefined,
  attribute: CloseDateAttribute | null,
): ProgressUpdateFormValues => {
  if (category === "audit") {
    const audit = record as Audit | undefined;
    return {
      status: audit?.status || "",
      statusActionOwner: audit?.statusActionOwner || "",
      statusDetail: audit?.statusDetail || "",
      effort: audit?.effort || "0",
      closeDate: audit?.auditCloseDateString || "",
    };
  }
  const map = (record as GaiaObject | undefined)?.customAttributesDataMap ?? {};
  const stored = attribute ? map[attribute] : undefined;
  return {
    status: map.status || "",
    statusActionOwner: map.statusActionOwner || "",
    statusDetail: map.statusDetail || "",
    effort: map.effort || "0",
    closeDate: stored ? toDateString(Number(stored)) : "",
  };
};

export const buildAuditUpdate = (
  audit: Audit,
  data: ProgressUpdateFormValues,
  customAttributes: CustomAttributes | undefined,
  progressUpdateAttachments: ProgressUpdateAttachment[] | undefined,
): UpdateAuditProgressRequest => {
  const update: UpdateAuditProgressRequest = {
    ...audit,
    status: data.status,
    statusActionOwner: data.statusActionOwner,
    statusDetail: data.statusDetail,
    effort: data.effort.toString(),
    ...(progressUpdateAttachments ? { progressUpdateAttachments } : {}),
  };
  if (data.closeDate) {
    update.auditCloseDateString = data.closeDate;
    update.auditCloseDate = toUnixEpochTime(data.closeDate);
  } else {
    delete update.auditCloseDateString;
    delete update.auditCloseDate;
  }
  if (update.entity) {
    update.entity = getEntityNameFromEntityCompositeKey(update.entity);
  }
  if (customAttributes && update[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]) {
    update[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME] =
      generateDynamicAttributesDataMap(
        customAttributes,
        update[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME],
      );
  }
  return update;
};

export const buildGaiaObjectUpdate = (
  gaiaObject: GaiaObject,
  data: ProgressUpdateFormValues,
  attribute: CloseDateAttribute | null,
  progressUpdateAttachments: ProgressUpdateAttachment[] | undefined,
): UpdateGaiaObjectProgressRequest => {
  const map: Record<string, string> = {
    ...gaiaObject.customAttributesDataMap,
    status: data.status,
    statusActionOwner: data.statusActionOwner,
    statusDetail: data.statusDetail,
    effort: data.effort.toString(),
  };
  if (attribute) {
    if (data.closeDate) {
      map[attribute] = toUnixEpochTime(data.closeDate).toString();
    } else {
      delete map[attribute];
    }
  }
  return {
    gaiaObjectId: gaiaObject.gaiaObjectId,
    gaiaObject: { ...gaiaObject, customAttributesDataMap: map },
    ...(progressUpdateAttachments ? { progressUpdateAttachments } : {}),
  };
};

export const withoutCloseDate = (
  results: ClosingRequirementResult[] | undefined,
) =>
  results?.map((result) =>
    result.requirementType === "CLOSE_DATE_SET"
      ? { ...result, met: false, reason: "Close date is not set" }
      : result,
  );

interface UnmetErrorBody {
  unmetRequirements?: ClosingRequirementResult[];
}

export const toSaveFailure = (
  error: unknown,
  fallback: string,
): ProgressUpdateSaveResult => {
  const body = (error as { data?: UnmetErrorBody } | undefined)?.data;
  if (body?.unmetRequirements?.length) {
    return { outcome: "not-met", unmetRequirements: body.unmetRequirements };
  }
  return { outcome: "failed", message: getQueryErrorMessage(error, fallback) };
};
